import React, { useEffect } from "react"

import classNames from "classnames"

import { useEvent } from "shared/lib/useEvent"
import { WithClassName } from "shared/types/WithClassName"
import { Button } from "shared/ui/buttons/Button"

import { GlobalSearchSelectableOption } from "features/global-search/types/GlobalSearch"

import { useGlobalSearchContext } from "../../GlobalSearchContext"
import { useGlobalSearchNavigationContext } from "../../GlobalSearchNavigationContext"

interface GlobalSearchListItemProps extends WithClassName {
  index: number
  option: GlobalSearchSelectableOption
  isTabActive: boolean
  children: React.ReactNode
}

export const GlobalSearchListItem = React.memo(
  ({
    index,
    option,
    isTabActive,
    className,
    children
  }: GlobalSearchListItemProps) => {
    const {
      activeIndex,
      getItemId,
      activateItem,
      keyboardActiveRef,
      register,
      unregister,
      updateOption
    } = useGlobalSearchNavigationContext()
    const { onChange } = useGlobalSearchContext()
    const ref = React.useRef<HTMLButtonElement>(null)

    useEffect(() => {
      if (!isTabActive || !ref.current) return undefined
      register(index, ref.current)
      return () => unregister(index)
    }, [index, isTabActive, register, unregister])

    useEffect(() => {
      if (!isTabActive) return
      updateOption(index, option)
    }, [index, isTabActive, option, updateOption])

    const isActive = activeIndex === index

    const handleMouseMove = useEvent(() => {
      if (!keyboardActiveRef.current) activateItem(index)
    })

    const handleClick = useEvent(() => onChange(option))

    return (
      <Button
        ref={ref}
        id={getItemId(index)}
        role="option"
        tabIndex={-1}
        aria-selected={isActive}
        className={classNames("global-search__list-item", className)}
        data-highlight={isActive ? "" : undefined}
        onMouseMove={handleMouseMove}
        onClick={handleClick}
      >
        {children}
      </Button>
    )
  }
)
